<template>
  <div class="admin-container">
    <a-layout>
      <a-layout-header class="admin-header">
        <div class="header-content">
          <h2>后台管理系统</h2>
          <div class="user-info">
            <span>当前用户：{{ currentUser.username }}</span>
            <a-button type="link" @click="handleLogout">退出登录</a-button>
          </div>
        </div>
      </a-layout-header>
      <a-layout-content class="admin-content">
        <a-tabs default-active-key="users">
          <!-- 用户管理标签页 -->
          <a-tab-pane key="users" tab="用户管理">
            <a-card>
              <div slot="title">
                <a-button type="primary" @click="showAddModal">
                  <a-icon type="plus" /> 添加用户
                </a-button>
              </div>
              
              <a-table
                :columns="columns"
                :data-source="users"
                :pagination="{ pageSize: 10 }"
                :loading="loading"
                row-key="id"
              >
                <template slot="role" slot-scope="text">
                  <a-tag :color="text === 'admin' ? 'red' : 'blue'">
                    {{ text === 'admin' ? '管理员' : '普通用户' }}
                  </a-tag>
                </template>
                <template slot="is_approved" slot-scope="text">
                  <a-tag :color="text === 1 ? 'green' : 'orange'">
                    {{ text === 1 ? '已审批' : '待审批' }}
                  </a-tag>
                </template>
                <template slot="is_disabled" slot-scope="text">
                  <a-tag :color="text === 1 ? 'red' : 'green'">
                    {{ text === 1 ? '已禁用' : '正常' }}
                  </a-tag>
                </template>
                <template slot="action" slot-scope="text, record">
                  <a-button 
                    v-if="record.role !== 'admin' && record.is_approved === 0" 
                    type="link" 
                    @click="handleApprove(record)"
                    style="color: #52c41a;"
                  >
                    审批
                  </a-button>
                  <a-button 
                    v-if="record.role !== 'admin'" 
                    type="link" 
                    @click="handleToggleDisable(record)"
                    :style="{ color: record.is_disabled === 1 ? '#52c41a' : '#ff4d4f' }"
                  >
                    {{ record.is_disabled === 1 ? '启用' : '禁用' }}
                  </a-button>
                  <a-button type="link" @click="showEditModal(record)">编辑</a-button>
                  <a-button type="link" danger @click="handleDelete(record)">删除</a-button>
                </template>
              </a-table>
            </a-card>
          </a-tab-pane>

          <!-- 激活码管理标签页 -->
          <a-tab-pane key="activation-codes" tab="激活码管理">
            <a-card>
              <div slot="title">
                <a-button type="primary" @click="showAddActivationCodeModal">
                  <a-icon type="plus" /> 生成激活码
                </a-button>
              </div>
              
              <a-table
                :columns="activationCodeColumns"
                :data-source="activationCodes"
                :pagination="{ pageSize: 10 }"
                :loading="activationCodeLoading"
                row-key="id"
              >
                <template slot="code" slot-scope="text">
                  <span style="font-family: monospace;">{{ text }}</span>
                  <a-button type="link" size="small" @click="copyToClipboard(text)" style="padding: 0 4px;">
                    <a-icon type="copy" />
                  </a-button>
                </template>
                <template slot="is_active" slot-scope="text">
                  <a-tag :color="text === 1 ? 'green' : 'red'">
                    {{ text === 1 ? '已激活' : '已禁用' }}
                  </a-tag>
                </template>
                <template slot="machine_info" slot-scope="text">
                  <span v-if="text" :title="text">{{ text.length > 30 ? text.substring(0, 30) + '...' : text }}</span>
                  <span v-else style="color: #999;">未绑定</span>
                </template>
                <template slot="activated_at" slot-scope="text">
                  <span v-if="text">{{ formatDate(text) }}</span>
                  <span v-else style="color: #999;">未激活</span>
                </template>
                <template slot="activated_by_username" slot-scope="text, record">
                  <span v-if="text">{{ text }}</span>
                  <span v-else-if="record.activated_by_phone">{{ record.activated_by_phone }}</span>
                  <span v-else style="color: #999;">-</span>
                </template>
                <template slot="activated_ip" slot-scope="text">
                  <span v-if="text" style="font-family: monospace;">{{ text }}</span>
                  <span v-else style="color: #999;">-</span>
                </template>
                <template slot="expires_at" slot-scope="text">
                  <span v-if="text">{{ formatDate(text) }}</span>
                  <span v-else style="color: #999;">永久有效</span>
                </template>
                <template slot="action" slot-scope="text, record">
                  <a-button type="link" @click="showEditActivationCodeModal(record)">编辑</a-button>
                  <a-button type="link" danger @click="handleDeleteActivationCode(record)">删除</a-button>
                </template>
              </a-table>
            </a-card>
          </a-tab-pane>
        </a-tabs>

        <!-- 添加/编辑用户弹窗 -->
        <a-modal
          :title="modalTitle"
          :visible="modalVisible"
          @ok="handleSubmit"
          @cancel="handleCancel"
          :confirmLoading="submitting"
        >
          <a-form :label-col="{ span: 6 }" :wrapper-col="{ span: 16 }">
            <a-form-item label="用户名">
              <a-input
                v-model="formData.username"
                :disabled="isEdit"
                placeholder="请输入用户名"
              />
            </a-form-item>
            <a-form-item label="密码">
              <a-input
                v-model="formData.password"
                type="password"
                placeholder="请输入密码"
              />
            </a-form-item>
            <a-form-item label="角色">
              <a-select v-model="formData.role" placeholder="请选择角色">
                <a-select-option value="user">普通用户</a-select-option>
                <a-select-option value="admin">管理员</a-select-option>
              </a-select>
            </a-form-item>
          </a-form>
        </a-modal>

        <!-- 添加/编辑激活码弹窗 -->
        <a-modal
          :title="activationCodeModalTitle"
          :visible="activationCodeModalVisible"
          @ok="handleActivationCodeSubmit"
          @cancel="handleActivationCodeCancel"
          :confirmLoading="activationCodeSubmitting"
        >
          <a-form :label-col="{ span: 6 }" :wrapper-col="{ span: 16 }">
            <a-form-item v-if="isEditActivationCode" label="激活码">
              <a-input
                v-model="activationCodeFormData.code"
                disabled
                placeholder="激活码"
              />
            </a-form-item>
            <a-form-item label="状态">
              <a-switch
                v-model="activationCodeFormData.is_active"
                checked-children="激活"
                un-checked-children="禁用"
              />
            </a-form-item>
            <a-form-item label="过期时间">
              <a-date-picker
                v-model="activationCodeFormData.expires_at"
                show-time
                format="YYYY-MM-DD HH:mm:ss"
                placeholder="选择过期时间（留空表示永久有效）"
                style="width: 100%"
              />
            </a-form-item>
            <a-form-item label="机器信息">
              <a-input
                v-model="activationCodeFormData.machine_info"
                :disabled="true"
                placeholder="机器信息（自动绑定）"
              />
            </a-form-item>
          </a-form>
        </a-modal>
      </a-layout-content>
    </a-layout>
  </div>
</template>

<script>
import api from '../api'
import moment from 'moment'

export default {
  name: 'Admin',
  data () {
    return {
      users: [],
      loading: false,
      modalVisible: false,
      modalTitle: '添加用户',
      isEdit: false,
      editingUser: null,
      submitting: false,
      formData: {
        username: '',
        password: '',
        role: 'user'
      },
      columns: [
        {
          title: 'ID',
          dataIndex: 'id',
          key: 'id',
          width: 80
        },
        {
          title: '用户名',
          dataIndex: 'username',
          key: 'username',
          width: 120
        },
        {
          title: '手机号',
          dataIndex: 'phone',
          key: 'phone',
          width: 120
        },
        {
          title: '角色',
          dataIndex: 'role',
          key: 'role',
          scopedSlots: { customRender: 'role' },
          width: 100
        },
        {
          title: '审批状态',
          dataIndex: 'is_approved',
          key: 'is_approved',
          scopedSlots: { customRender: 'is_approved' },
          width: 120
        },
        {
          title: '账户状态',
          dataIndex: 'is_disabled',
          key: 'is_disabled',
          scopedSlots: { customRender: 'is_disabled' },
          width: 120
        },
        {
          title: '注册时间',
          dataIndex: 'created_at',
          key: 'created_at',
          width: 180
        },
        {
          title: '操作',
          key: 'action',
          scopedSlots: { customRender: 'action' },
          width: 200
        }
      ],
      // 激活码管理相关
      activationCodes: [],
      activationCodeLoading: false,
      activationCodeModalVisible: false,
      activationCodeModalTitle: '生成激活码',
      isEditActivationCode: false,
      editingActivationCode: null,
      activationCodeSubmitting: false,
      activationCodeFormData: {
        code: '',
        is_active: true,
        expires_at: null,
        machine_info: ''
      },
      activationCodeColumns: [
        {
          title: 'ID',
          dataIndex: 'id',
          key: 'id',
          width: 80
        },
        {
          title: '激活码',
          dataIndex: 'code',
          key: 'code',
          scopedSlots: { customRender: 'code' },
          width: 200
        },
        {
          title: '状态',
          dataIndex: 'is_active',
          key: 'is_active',
          scopedSlots: { customRender: 'is_active' },
          width: 100
        },
        {
          title: '机器信息',
          dataIndex: 'machine_info',
          key: 'machine_info',
          scopedSlots: { customRender: 'machine_info' },
          width: 200
        },
        {
          title: '激活时间',
          dataIndex: 'activated_at',
          key: 'activated_at',
          scopedSlots: { customRender: 'activated_at' },
          width: 180
        },
        {
          title: '激活用户',
          dataIndex: 'activated_by_username',
          key: 'activated_by_username',
          scopedSlots: { customRender: 'activated_by_username' },
          width: 150
        },
        {
          title: '激活IP',
          dataIndex: 'activated_ip',
          key: 'activated_ip',
          scopedSlots: { customRender: 'activated_ip' },
          width: 150
        },
        {
          title: '过期时间',
          dataIndex: 'expires_at',
          key: 'expires_at',
          scopedSlots: { customRender: 'expires_at' },
          width: 180
        },
        {
          title: '创建时间',
          dataIndex: 'created_at',
          key: 'created_at',
          width: 180
        },
        {
          title: '操作',
          key: 'action',
          scopedSlots: { customRender: 'action' },
          width: 150
        }
      ]
    }
  },
  computed: {
    currentUser () {
      return this.$store.state.currentUser || {}
    }
  },
  methods: {
    showAddModal () {
      this.modalTitle = '添加用户'
      this.isEdit = false
      this.editingUser = null
      this.formData = {
        username: '',
        password: '',
        role: 'user'
      }
      this.modalVisible = true
    },
    showEditModal (record) {
      this.modalTitle = '编辑用户'
      this.isEdit = true
      this.editingUser = record
      this.formData = {
        username: record.username,
        password: '', // 编辑时不显示密码
        role: record.role
      }
      this.modalVisible = true
    },
    async handleSubmit () {
      if (!this.formData.username || !this.formData.password || !this.formData.role) {
        this.$message.warning('请填写完整信息！')
        return
      }
      
      this.submitting = true
      
      try {
        if (this.isEdit) {
          // 编辑用户
          const response = await api.updateUser(this.editingUser.id, {
            password: this.formData.password,
            role: this.formData.role
          })
          if (response.success) {
            this.$message.success('用户更新成功！')
            await this.loadUsers()
          }
        } else {
          // 添加用户
          const response = await api.addUser(this.formData)
          if (response.success) {
            this.$message.success('用户添加成功！')
            await this.loadUsers()
          }
        }
        
        this.submitting = false
        this.modalVisible = false
        this.formData = {
          username: '',
          password: '',
          role: 'user'
        }
      } catch (error) {
        this.$message.error(error.message || '操作失败')
        this.submitting = false
      }
    },
    handleCancel () {
      this.modalVisible = false
      this.formData = {
        username: '',
        password: '',
        role: 'user'
      }
    },
    async handleApprove (record) {
      const self = this
      this.$confirm({
        title: '确认审批',
        content: `确定要审批用户 "${record.username || record.phone}" 吗？审批后该用户即可登录使用系统。`,
        async onOk () {
          try {
            const response = await api.approveUser(record.id)
            if (response.success) {
              self.$message.success('用户审批成功！')
              await self.loadUsers()
            }
          } catch (error) {
            self.$message.error(error.message || '审批失败')
          }
        }
      })
    },
    async handleToggleDisable (record) {
      const self = this
      const action = record.is_disabled === 1 ? '启用' : '禁用'
      const actionDesc = record.is_disabled === 1 ? '启用后该用户可以正常登录使用系统' : '禁用后该用户将无法登录系统'
      
      this.$confirm({
        title: `确认${action}`,
        content: `确定要${action}用户 "${record.username || record.phone}" 吗？${actionDesc}。`,
        async onOk () {
          try {
            const response = await api.toggleUserDisable(record.id, record.is_disabled === 0)
            if (response.success) {
              self.$message.success(`用户${action}成功！`)
              await self.loadUsers()
            }
          } catch (error) {
            self.$message.error(error.message || `${action}失败`)
          }
        }
      })
    },
    async handleDelete (record) {
      const self = this
      this.$confirm({
        title: '确认删除',
        content: `确定要删除用户 "${record.username || record.phone}" 吗？`,
        async onOk () {
          try {
            const response = await api.deleteUser(record.id)
            if (response.success) {
              self.$message.success('用户删除成功！')
              await self.loadUsers()
            }
          } catch (error) {
            self.$message.error(error.message || '删除失败')
          }
        }
      })
    },
    async loadUsers () {
      this.loading = true
      try {
        const response = await api.getUsers()
        if (response.success) {
          this.users = response.data || []
        }
      } catch (error) {
        this.$message.error(error.message || '加载用户列表失败')
      } finally {
        this.loading = false
      }
    },
    async handleLogout () {
      try {
        // 调用后端登出API
        await api.logout()
      } catch (error) {
        console.error('登出失败:', error)
        // 即使API调用失败，也清除本地状态
      } finally {
        // 清除登录状态和session token
        this.$store.commit('setIsLoggedIn', false)
        this.$store.commit('setCurrentUser', null)
        this.$store.commit('setSessionToken', null)
        this.$router.push('/login')
      }
    },
    // 激活码管理方法
    showAddActivationCodeModal () {
      this.activationCodeModalTitle = '生成激活码'
      this.isEditActivationCode = false
      this.editingActivationCode = null
      this.activationCodeFormData = {
        code: '',
        is_active: true,
        expires_at: null,
        machine_info: ''
      }
      this.activationCodeModalVisible = true
    },
    showEditActivationCodeModal (record) {
      this.activationCodeModalTitle = '编辑激活码'
      this.isEditActivationCode = true
      this.editingActivationCode = record
      this.activationCodeFormData = {
        code: record.code,
        is_active: record.is_active === 1,
        expires_at: record.expires_at ? moment(record.expires_at) : null,
        machine_info: record.machine_info || ''
      }
      this.activationCodeModalVisible = true
    },
    async handleActivationCodeSubmit () {
      this.activationCodeSubmitting = true
      
      try {
        if (this.isEditActivationCode) {
          // 编辑激活码
          const updateData = {
            is_active: this.activationCodeFormData.is_active
          }
          
          if (this.activationCodeFormData.expires_at) {
            updateData.expires_at = moment(this.activationCodeFormData.expires_at).format('YYYY-MM-DD HH:mm:ss')
          } else {
            updateData.expires_at = null
          }
          
          const response = await api.updateActivationCode(this.editingActivationCode.id, updateData)
          if (response.success) {
            this.$message.success('激活码更新成功！')
            await this.loadActivationCodes()
          }
        } else {
          // 生成激活码
          const generateData = {}
          if (this.activationCodeFormData.expires_at) {
            generateData.expires_at = moment(this.activationCodeFormData.expires_at).format('YYYY-MM-DD HH:mm:ss')
          }
          
          const response = await api.generateActivationCode(generateData)
          if (response.success) {
            this.$message.success('激活码生成成功！')
            await this.loadActivationCodes()
          }
        }
        
        this.activationCodeSubmitting = false
        this.activationCodeModalVisible = false
        this.activationCodeFormData = {
          code: '',
          is_active: true,
          expires_at: null,
          machine_info: ''
        }
      } catch (error) {
        this.$message.error(error.message || '操作失败')
        this.activationCodeSubmitting = false
      }
    },
    handleActivationCodeCancel () {
      this.activationCodeModalVisible = false
      this.activationCodeFormData = {
        code: '',
        is_active: true,
        expires_at: null,
        machine_info: ''
      }
    },
    async handleDeleteActivationCode (record) {
      const self = this
      this.$confirm({
        title: '确认删除',
        content: `确定要删除激活码 "${record.code}" 吗？`,
        async onOk () {
          try {
            const response = await api.deleteActivationCode(record.id)
            if (response.success) {
              self.$message.success('激活码删除成功！')
              await self.loadActivationCodes()
            }
          } catch (error) {
            self.$message.error(error.message || '删除失败')
          }
        }
      })
    },
    async loadActivationCodes () {
      this.activationCodeLoading = true
      try {
        const response = await api.getActivationCodes()
        if (response.success) {
          this.activationCodes = response.data || []
        }
      } catch (error) {
        this.$message.error(error.message || '加载激活码列表失败')
      } finally {
        this.activationCodeLoading = false
      }
    },
    formatDate (dateString) {
      if (!dateString) return ''
      return moment(dateString).format('YYYY-MM-DD HH:mm:ss')
    },
    copyToClipboard (text) {
      const textarea = document.createElement('textarea')
      textarea.value = text
      textarea.style.position = 'fixed'
      textarea.style.opacity = '0'
      document.body.appendChild(textarea)
      textarea.select()
      try {
        document.execCommand('copy')
        this.$message.success('激活码已复制到剪贴板')
      } catch (err) {
        this.$message.error('复制失败，请手动复制')
      }
      document.body.removeChild(textarea)
    }
  },
  mounted () {
    // 检查登录状态
    if (!this.$store.state.isLoggedIn) {
      this.$router.push('/login')
      return
    }
    
    // 检查是否为管理员
    if (this.currentUser.role !== 'admin') {
      this.$message.warning('您没有权限访问此页面！')
      this.$router.push('/')
      return
    }
    
    // 加载用户列表和激活码列表
    this.loadUsers()
    this.loadActivationCodes()
  }
}
</script>

<style scoped>
.admin-container {
  min-height: 100vh;
}

.admin-header {
  background: #001529;
  padding: 0 24px;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 64px;
}

.header-content h2 {
  color: #fff;
  margin: 0;
  font-size: 20px;
}

.user-info {
  color: #fff;
  display: flex;
  align-items: center;
  gap: 16px;
}

.user-info .ant-btn-link {
  color: #fff;
  padding: 0;
}

.admin-content {
  padding: 24px;
  background: #f0f2f5;
  min-height: calc(100vh - 64px);
}
</style>

